import { useCallback, useState, type ChangeEvent } from 'react';

import { ChevronDown, Close } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import type { VariantProps } from 'class-variance-authority';

import { Button } from '../../lib/button.jsx';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '../../lib/input-group.jsx';
import { type InputPropsType } from '../../lib/input.jsx';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '../../lib/popover.jsx';
import { inputVariants } from '../../lib/variants.jsx';

type ComboboxInputProps = Readonly<{
  /** INPUT PROPS */
  /**
   * The id of the combobox input
   */
  id?: string;
  /**
   * The value of the combobox input
   */
  value?: string;
  /**
   * The start icon of the combobox input
   */
  startIcon?: React.ReactNode;
  /**
   * The placeholder of the combobox input
   */
  placeholder?: string;
  /**
   * The disabled state of the combobox input
   */
  disabled?: boolean;
  /**
   * The readOnly state of the combobox input
   */
  readOnly?: boolean;
  /**
   * The size of the combobox input
   */
  size?: 'medium' | 'small';
  /**
   * The color of the combobox input
   */
  color?: 'default' | 'warning' | 'error';
  /**
   * If true, the combobox input will have a clear button
   */
  clearable?: boolean;
  /**
   * Input props
   */
  inputProps?: InputPropsType;

  /** POPOVER PROPS */
  /**
   * The alignment of the combobox popover
   */
  align?: 'start' | 'center' | 'end';
  /**
   * The children to be rendered inside the popover
   */
  children?: React.ReactNode;
  /**
   * If true, the combobox popover will be open
   */
  open?: boolean;
  /**
   * The onOpenChange event handler for the combobox popover
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * The class name for the combobox popover content
   */
  popoverClassName?: string;
  /**
   * If true, the container for the portal will be in the document.body
   */
  isPortal?: boolean;
  /**
   * The portal container element for the popover
   */
  container?: React.ComponentProps<typeof PopoverPortal>['container'];
}>;

function ComboboxInput({
  id,
  children,
  align = 'start',
  placeholder,
  value,
  open,
  disabled,
  readOnly,
  startIcon,
  size = 'medium',
  color = 'default',
  onOpenChange,
  popoverClassName,
  isPortal,
  container,
  inputProps,
  clearable = false,
}: ComboboxInputProps & VariantProps<typeof inputVariants>) {
  const [internalContainer, setInternalContainer] =
    useState<HTMLDivElement | null>(null);
  const [internalValue, setInternalValue] = useState<string>(value || '');
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    setInternalContainer((prev) => (prev === node ? prev : node));
  }, []);
  const effectiveContainer = container ?? internalContainer;
  const portalContainer = isPortal ? document.body : effectiveContainer;
  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (inputProps?.onChange) {
      inputProps.onChange({
        target: { value: '' },
      } as ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <Popover open={open} onOpenChange={readOnly ? undefined : onOpenChange}>
      <PopoverTrigger asChild>
        <InputGroup
          className={'cursor-pointer caret-transparent-main'}
          size={size}
          color={color}
        >
          <InputGroupInput
            id={id}
            type={'text'}
            className="cursor-pointer select-none"
            size={size}
            data-testid="combobox-input"
            value={value ?? internalValue}
            onChange={(e) => setInternalValue(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            data-dd-action-name={`"${placeholder}" input`}
            {...inputProps}
          />
          {startIcon && (
            <InputGroupAddon align="inline-start">{startIcon}</InputGroupAddon>
          )}
          <InputGroupAddon align="inline-end">
            {clearable && value && (
              <Button
                icon
                size="small"
                variant="text"
                onClick={handleClear}
                aria-label="clear-button"
                data-dd-action-name="Clear combobox input"
              >
                <Close />
              </Button>
            )}
          </InputGroupAddon>
          <InputGroupAddon align="inline-end">
            <ChevronDown />
          </InputGroupAddon>
        </InputGroup>
      </PopoverTrigger>
      {portalContainer && (
        <PopoverPortal container={portalContainer}>
          <PopoverContent
            className={cn(
              popoverClassName,
              'w-(--radix-popover-trigger-width)',
            )}
            align={align}
            data-testid="combobox-input-popover"
          >
            {children}
          </PopoverContent>
        </PopoverPortal>
      )}
      <div ref={setContainerRef} />
    </Popover>
  );
}

export default ComboboxInput;
